import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { EnterpriseSidebar } from "./EnterpriseDashboard";
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from "recharts";
import { useCyclePicker, CyclePickerBar } from "../hooks/useCyclePicker";

const VERDICT_COLORS = {
    Retain:   "#34D399",  // emerald-400
    Retrain:  "#FBBF24",  // amber-400
    Redeploy: "#F87171",  // red-400
};

export default function WorkforceHeatmap() {
    const { api } = useAuth();
    const navigate = useNavigate();
    const picker = useCyclePicker();
    const { cycleId } = picker;

    const [workers, setWorkers] = useState([]);
    const [summary, setSummary] = useState({});
    const [filters, setFilters] = useState({ departments: [], regions: [], cadres: [] });
    const [department, setDepartment] = useState("");
    const [region, setRegion] = useState("");
    const [verdict, setVerdict] = useState("");
    const [cadre, setCadre] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!cycleId) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams();
                if (department) params.set("department", department);
                if (region) params.set("region", region);
                if (verdict) params.set("verdict", verdict);
                if (cadre) params.set("cadre", cadre);
                const res = await api.get(`/v2/dashboard/heatmap/${cycleId}?${params}`);
                if (cancelled) return;
                setWorkers(res.data.workers || []);
                setSummary(res.data.summary || {});
                setFilters(res.data.filters || { departments: [], regions: [], cadres: [] });
            } catch (e) {
                console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [api, cycleId, department, region, verdict, cadre]);

    const chartData = useMemo(() =>
        workers
            .filter((w) => w.capability_composite !== null && w.displacement_composite !== null)
            .map((w) => ({
                x: w.capability_composite,
                y: w.displacement_composite,
                name: w.full_name,
                role: w.job_title,
                dept: w.department,
                region: w.region,
                verdict: w.verdict || "Unassigned",
                cadre: w.cadre,
                id: w.candidate_id,
            })),
        [workers]
    );

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload?.length) {
            const d = payload[0].payload;
            return (
                <div className="bg-card border border-border/40 p-3 shadow-xl text-xs space-y-1" style={{ pointerEvents: "none" }}>
                    <p className="text-white font-semibold">{d.name}</p>
                    <p className="text-gray-400">{d.role}</p>
                    <p className="text-gray-500">{d.dept} · {d.region}</p>
                    <p className="text-gray-500">Cadre: {d.cadre || "—"}</p>
                    <div className="pt-1 border-t border-border/40 mt-1 space-y-0.5">
                        <p className="text-gray-400">Capability: <span className="text-white font-mono">{d.x?.toFixed(1)}</span></p>
                        <p className="text-gray-400">Displacement: <span className="text-white font-mono">{d.y?.toFixed(1)}</span></p>
                        <p className="text-gray-400">Verdict: <span style={{ color: VERDICT_COLORS[d.verdict] || "#9CA3AF" }}>{d.verdict}</span></p>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="flex">
            <EnterpriseSidebar current="/enterprise/heatmap" />
            <main className="ml-56 flex-1 p-8 min-h-screen" data-testid="workforce-heatmap">
                <div className="max-w-6xl space-y-6">
                    <div>
                        <h1 className="text-2xl font-display font-bold text-white">Workforce Heatmap</h1>
                        <p className="text-sm text-gray-500 mt-1 font-body">Capability vs Displacement · verdict-coloured</p>
                    </div>

                    <CyclePickerBar picker={picker} />

                    {/* Filters */}
                    <div className="bg-card border border-border/40 p-4 flex flex-wrap gap-3 items-center text-sm" data-testid="heatmap-filters">
                        <select value={department} onChange={(e) => setDepartment(e.target.value)}
                            className="bg-black border border-border/40 px-3 py-1.5 text-white" data-testid="filter-department">
                            <option value="">All Departments</option>
                            {filters.departments.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                        <select value={region} onChange={(e) => setRegion(e.target.value)}
                            className="bg-black border border-border/40 px-3 py-1.5 text-white" data-testid="filter-region">
                            <option value="">All Regions</option>
                            {filters.regions.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                        <select value={cadre} onChange={(e) => setCadre(e.target.value)}
                            className="bg-black border border-border/40 px-3 py-1.5 text-white" data-testid="filter-cadre">
                            <option value="">All Cadres</option>
                            {filters.cadres.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                        <select value={verdict} onChange={(e) => setVerdict(e.target.value)}
                            className="bg-black border border-border/40 px-3 py-1.5 text-white" data-testid="filter-verdict">
                            <option value="">All Verdicts</option>
                            <option value="Retain">Retain</option>
                            <option value="Retrain">Retrain</option>
                            <option value="Redeploy">Redeploy</option>
                        </select>
                        <div className="ml-auto text-xs text-gray-500 font-mono">
                            {summary.showing ?? 0} / {summary.total ?? 0} candidates · {summary.analysed ?? 0} analysed
                        </div>
                    </div>

                    {/* Summary tiles */}
                    <div className="grid grid-cols-3 gap-4">
                        {["Retain", "Retrain", "Redeploy"].map((v) => {
                            const count = (summary.verdicts && summary.verdicts[v]) || 0;
                            const color = VERDICT_COLORS[v];
                            return (
                                <div key={v} className="bg-card border border-border/40 p-4" data-testid={`heatmap-summary-${v.toLowerCase()}`}>
                                    <p className="text-xs uppercase tracking-wider" style={{ color }}>{v}</p>
                                    <p className="text-2xl font-mono font-bold mt-1" style={{ color }}>{count}</p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Scatter chart */}
                    <div className="bg-card border border-border/40 p-6" data-testid="heatmap-scatter">
                        <div style={{ width: "100%", height: 480 }}>
                            <ResponsiveContainer>
                                <ScatterChart margin={{ top: 16, right: 16, bottom: 40, left: 40 }}>
                                    <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                                    <XAxis type="number" dataKey="x" domain={[0, 100]}
                                        label={{ value: "Capability Composite", position: "insideBottom", offset: -20, fill: "#9CA3AF", fontSize: 12 }}
                                        tick={{ fill: "#9CA3AF", fontSize: 11 }} stroke="#374151" />
                                    <YAxis type="number" dataKey="y" domain={[0, 100]}
                                        label={{ value: "Displacement Composite", angle: -90, position: "insideLeft", offset: 10, fill: "#9CA3AF", fontSize: 12 }}
                                        tick={{ fill: "#9CA3AF", fontSize: 11 }} stroke="#374151" />
                                    <ReferenceLine x={50} stroke="#374151" strokeDasharray="3 3" />
                                    <ReferenceLine y={50} stroke="#374151" strokeDasharray="3 3" />
                                    <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#555" }} />
                                    <Scatter data={chartData} onClick={(d) => d?.id && navigate(`/enterprise/worker/${d.id}`)}>
                                        {chartData.map((d, i) => (
                                            <Cell key={i} fill={VERDICT_COLORS[d.verdict] || "#6B7280"} style={{ cursor: "pointer" }} />
                                        ))}
                                    </Scatter>
                                </ScatterChart>
                            </ResponsiveContainer>
                        </div>
                        {loading && <p className="text-xs text-gray-500 mt-2">Loading…</p>}
                        <div className="flex items-center gap-4 mt-4 text-xs text-gray-500 font-mono">
                            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full inline-block" style={{ background: VERDICT_COLORS.Retain }} /> Retain</span>
                            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full inline-block" style={{ background: VERDICT_COLORS.Retrain }} /> Retrain</span>
                            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full inline-block" style={{ background: VERDICT_COLORS.Redeploy }} /> Redeploy</span>
                            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full inline-block bg-gray-500" /> Unassigned</span>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-card border border-border/40" data-testid="heatmap-table">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-gray-500 uppercase tracking-wider border-b border-border/40">
                                    <th className="text-left px-4 py-2">Name</th>
                                    <th className="text-left px-4 py-2">Role</th>
                                    <th className="text-left px-4 py-2">Cadre</th>
                                    <th className="text-right px-4 py-2">Capability</th>
                                    <th className="text-right px-4 py-2">Displacement</th>
                                    <th className="text-left px-4 py-2">Verdict</th>
                                </tr>
                            </thead>
                            <tbody>
                                {workers.map((w) => (
                                    <tr key={w.candidate_id}
                                        onClick={() => navigate(`/enterprise/worker/${w.candidate_id}`)}
                                        className="border-b border-border/20 hover:bg-white/5 cursor-pointer"
                                        data-testid={`heatmap-row-${w.candidate_id}`}>
                                        <td className="px-4 py-2 text-white">{w.full_name}</td>
                                        <td className="px-4 py-2 text-gray-400">{w.job_title}</td>
                                        <td className="px-4 py-2 text-gray-500">{w.cadre || "—"}</td>
                                        <td className="px-4 py-2 text-right font-mono text-white">{w.capability_composite !== null ? w.capability_composite.toFixed(1) : "—"}</td>
                                        <td className="px-4 py-2 text-right font-mono text-white">{w.displacement_composite !== null ? w.displacement_composite.toFixed(1) : "—"}</td>
                                        <td className="px-4 py-2">
                                            {w.verdict ? (
                                                <span className="px-2 py-0.5 text-xs font-semibold" style={{ color: VERDICT_COLORS[w.verdict], border: `1px solid ${VERDICT_COLORS[w.verdict]}33` }}>
                                                    {w.verdict}
                                                </span>
                                            ) : <span className="text-gray-600">—</span>}
                                        </td>
                                    </tr>
                                ))}
                                {workers.length === 0 && (
                                    <tr><td colSpan="6" className="px-4 py-6 text-center text-gray-600">No candidates in this cycle.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
}
